'use strict';

import axios from 'axios';
import { initGoogleMapsAutocomplete } from '@jspartials/google-maps-autocomplete-utils';
import {
  listenDOMEvent,
  createElement,
  appendElementContent
} from '@jspartials/dom-api-utils';
import { customEvent } from '@jspartials/general-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  toggleClassToElement
} from '@jspartials/class-list-utils';
import { pushOnDataLayer } from '@jspartials/gtm-utils';

// Selectors
const selector = {
  wrapper: '.js-pick-up-stores-wrapper',
  form: '.js-pick-up-stores-form',
  list: '.js-pick-up-stores-list',
  listResults: '.js-pick-up-stores-list-results',
  message: '.js-store-delivery-message',
  suggestions: {
    wrapper: '.js-suggested-addresses',
    list: '.js-suggested-addresses__list'
  },
  input: {
    search: '.js-pick-up-stores-input-search',
    latitude: '.js-pick-up-stores-input-latitude',
    longitude: '.js-pick-up-stores-input-longitude',
    countryCode: '.js-pick-up-stores-input-country-code',
    postalCode: '.js-pick-up-stores-input-postal-code'
  },
  store: {
    card: '.js-pick-up-store-card',
    button: '.js-pick-up-store-button',
    notOptical: '.js-is-not-optic-card'
  },
  results: {
    wrapper: '.js-pick-up-stores-results-wrapper',
    message: '.js-pick-up-stores-results-message'
  },
  filter: {
    wrapper: '.js-pick-up-stores-filter-wrapper',
    input: '.js-pick-up-stores-filter-input',
    optical: '.js-checkbox-filter-optics-list'
  },
  button: {
    submit: '.js-pick-up-stores-button-submit',
    geolocation: '.js-pick-up-stores-button-geolocation'
  }
};

// Elements
const element = {
  wrapper: null,
  form: null,
  list: null,
  listResults: null,
  message: null,
  suggestions: {
    wrapper: null,
    list: null
  },
  input: {
    search: null,
    latitude: null,
    longitude: null,
    countryCode: null,
    postalCode: null
  },
  store: {
    card: null,
    button: null,
    notOptical: null
  },
  results: {
    wrapper: null,
    message: null
  },
  filter: {
    wrapper: null,
    input: null,
    optical: null
  },
  button: {
    submit: null,
    geolocation: null
  }
};

// Variables
const environments = {
  pdp: 'pdp',
  checkout: 'checkout'
};
const requestAutocomplete = {
  input: '',
  sessionToken: ''
};
const sessionAutocomplete = {
  token: null,
  closed: false
};
let customEventListenerForForceCloseSessionActive = false;
let lastPrediction = null;
let environment = '';
let AutocompleteSessionToken = null;
let AutocompleteSuggestion = null;
let selectedIndexForSuggestionsList = 0;
let controller = null;
const minCharactersForAutocomplete =
  globalVariables.googleMaps.minCharactersForAutocomplete;
let debounceTimer = null;
const debounceDelay = 400;
const lastSearchedValueForAutocomplete = '';

// Functions

/**
 * Initialize the pick-up stores locator component
 * @returns {void}
 */
const initPickUpStoresLocator = () => {
  initElements();
  setEnvironment();
  listenButtonSubmit();
  listenButtonGeolocation();
  listenCloseSuggestions();
  setGoogleMapsAutocomplete();
  launchForCurrentStoreSelected();

  if (!customEventListenerForForceCloseSessionActive) {
    document.addEventListener(
      globalVariables.customEvent.forceCloseSessionAutocompleteStoresLocator,
      function () {
        forceCloseSessionWithLastPrediction();
      }
    );
    customEventListenerForForceCloseSessionActive = true;
  }
};

/**
 * Initialize the elements of the component
 * @returns {void}
 */
const initElements = () => {
  element.wrapper = document.querySelector(selector.wrapper) || null;
  element.form = document.querySelector(selector.form) || null;
  element.list = document.querySelector(selector.list) || null;
  element.listResults = document.querySelector(selector.listResults) || null;
  element.message = document.querySelector(selector.message) || null;
  // Suggestions
  if (element.form) {
    element.suggestions.wrapper =
      element.form.querySelector(selector.suggestions.wrapper) || null;
    element.suggestions.list =
      element.form.querySelector(selector.suggestions.list) || null;
  }
  // Inputs
  element.input.search = document.querySelector(selector.input.search) || null;
  element.input.latitude =
    document.querySelector(selector.input.latitude) || null;
  element.input.longitude =
    document.querySelector(selector.input.longitude) || null;
  element.input.countryCode =
    document.querySelector(selector.input.countryCode) || null;
  element.input.postalCode =
    document.querySelector(selector.input.postalCode) || null;
  // Results
  element.results.wrapper =
    document.querySelector(selector.results.wrapper) || null;
  element.results.message =
    document.querySelector(selector.results.message) || null;
  // Filter
  element.filter.wrapper =
    document.querySelector(selector.filter.wrapper) || null;
  element.filter.input = document.querySelector(selector.filter.input) || null;
  element.filter.optical =
    document.querySelector(selector.filter.optical) || null;
  // Buttons
  element.button.submit =
    document.querySelector(selector.button.submit) || null;
  element.button.geolocation =
    document.querySelector(selector.button.geolocation) || null;
};

/**
 * Set the environment of the component (PDP or Checkout)
 * @returns {void}
 */
const setEnvironment = () => {
  const environmentElement =
    element.form.closest('[data-pick-up-store-environment]') || null;

  if (environmentElement) {
    environment = environmentElement.dataset.pickUpStoreEnvironment;
  }
};

/**
 * Set the Google Maps Autocomplete component and its dependencies
 * @returns {void}
 */
const setGoogleMapsAutocomplete = async () => {
  const googleMapsAutocompleteLoader = await initGoogleMapsAutocomplete();

  requestAutocomplete.includedRegionCodes = [
    googleMapsAutocompleteLoader.countryCode
  ];
  requestAutocomplete.language = googleMapsAutocompleteLoader.language;

  googleMapsAutocompleteLoader.loader.load().then(async () => {
    ({ AutocompleteSessionToken, AutocompleteSuggestion } =
      await google.maps.importLibrary('places'));

    listenInputSearch();
  });
};

/**
 * Initialize the store cards elements
 * @returns {void}
 */
const initStoreCards = () => {
  element.store.card = document.querySelectorAll(selector.store.card) || [];
  element.store.button = document.querySelectorAll(selector.store.button) || [];
  element.store.notOptical =
    document.querySelectorAll(selector.store.notOptical) || [];

  listenStoreCards();
  listenStoresFilter();
};

/**
 * Listen the store cards elements events
 * @returns {void}
 */
const listenStoreCards = () => {
  // Listen store card click
  element.store.card.forEach((card) => {
    card.addEventListener('click', (event) => {
      if (!event.target.closest(selector.store.button)) {
        handleStoreCardClick(card);
      }
    });
  });

  // Listen store button click
  element.store.button.forEach((button) => {
    button.addEventListener('click', handleStoreButtonClick);
  });
};

/**
 * Listen the button submit event and handle it
 * @returns {void}
 */
const listenButtonSubmit = () => {
  if (element.button.submit) {
    listenDOMEvent(element.button.submit, 'click', () => {
      getSelectedOrFirstPrediction();
    });
  }
};

/**
 * Listen the input search events
 * @returns {void}
 */
const listenInputSearch = () => {
  if (element.input.search) {
    listenDOMEvent(element.input.search, 'input', debouncedHandleInputSearch);
    listenDOMEvent(element.input.search, 'focusin', debouncedHandleInputSearch);
    listenDOMEvent(element.input.search, 'keydown', handleInputKeyDown);
  }
};

/**
 * Listen the close suggestions list event
 * @returns {void}
 */
const listenCloseSuggestions = () => {
  listenDOMEvent(document, 'click', (event) => {
    if (!element.suggestions.wrapper.contains(event.target)) {
      closeSuggestionsList();
    }
  });
};

/**
 * Handle the form submit event
 * @returns {void}
 */
const handleFormSubmit = () => {
  const formData = getFormData();

  setLoader(true);

  axios
    .post(globalVariables.endpoint.findStores, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then((response) => {
      const { data } = response;
      const { html = '', resultsMessage = '' } = data;

      paintStoresList(html);
      updateResultsMessage(resultsMessage);
    })
    .catch((error) => {
      console.error(error);
    })
    .then(() => {
      setLoader(false);
    });
};

/**
 * Debounced handle the input search event
 * @param {Event} event - The input search event
 * @returns {void}
 */
const debouncedHandleInputSearch = (event) => {
  const currentValue = element.input.search.value.trim();

  if (
    event.type === 'focusin' &&
    currentValue === lastSearchedValueForAutocomplete
  ) {
    return;
  }

  clearTimeout(debounceTimer);

  debounceTimer = setTimeout(() => {
    handleInputSearch();
  }, debounceDelay);
};

/**
 * Handle the input search event
 * @param {Event} event - The input search event
 * @returns {void}
 */
const handleInputSearch = async () => {
  const currentValue = element.input.search.value.trim();

  if (currentValue.length >= minCharactersForAutocomplete) {
    if (!requestAutocomplete.sessionToken) {
      refreshSessionToken();
    }
    requestAutocomplete.input = currentValue;

    const suggestions = await getSuggestions();

    showSuggestions(suggestions);
  } else {
    closeSuggestionsList();
  }
};

/**
 * Handle the input key down event
 * @param {Event} event - The input key down event
 * @returns {void}
 */
const handleInputKeyDown = (event) => {
  const key = event.key;

  if (key === 'Enter') {
    event.preventDefault();
    getSelectedOrFirstPrediction();
  }
};

/**
 * Get the autocomplete suggestions from the input search
 * @returns {Promise<AutocompleteSuggestion[]>} The autocomplete suggestions
 */
const getSuggestions = async () => {
  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(
      requestAutocomplete
    );

  if (suggestions.length > 0) {
    lastPrediction = suggestions[0];
  }

  return suggestions;
};

/**
 * Show the autocomplete suggestions in the suggestions list element
 * @param {AutocompleteSuggestion[]} suggestions - The autocomplete suggestions
 * @returns {void}
 */
const showSuggestions = async (suggestions) => {
  const listElements = [];

  await Promise.all(
    suggestions.map(async (suggestion) => {
      const place = suggestion.placePrediction;
      const placeText = place.text;
      const suggestionText = placeText.text;
      const suggestionMatches = placeText.matches;
      const suggestionTextFormatted = formatSuggestionText(
        suggestionText,
        suggestionMatches
      );
      const liElement = createElement({
        tag: 'li',
        content: `&#128205; ${suggestionTextFormatted}`,
        classes: [
          'tw-cursor-pointer',
          'tw-py-2',
          'tw-px-4',
          'tw-text-sm',
          'tw-border-b',
          'tw-border-gray-200',
          'tw-border-dashed',
          'hover:tw-bg-gray-100',
          '[&.is-selected]:tw-bg-gray-100'
        ]
      });

      listenDOMEvent(liElement, 'click', () => {
        lastPrediction = suggestion;
        onPlaceSelected(place.toPlace());
      });

      listElements.push(liElement);
    })
  );

  paintSuggestions(listElements);
};

/**
 * Format the suggestion text with the highlighted text
 * @param {string} text - The suggestion text
 * @param {object[]} ranges - The highlighted text ranges
 * @returns {string} The formatted suggestion text with the highlighted text
 */
const formatSuggestionText = (text, ranges) => {
  let highlightedText = '';
  let lastIndex = 0;

  ranges.forEach((range) => {
    const { startOffset, endOffset } = range;

    highlightedText += text.slice(lastIndex, startOffset);

    highlightedText += `<strong>${text.slice(startOffset, endOffset)}</strong>`;

    lastIndex = endOffset;
  });

  highlightedText += text.slice(lastIndex);

  return highlightedText;
};

/**
 * Paint the suggestions list with the new content
 * @param {HTMLElement[]} newContent - The new content to paint
 * @returns {void}
 */
const paintSuggestions = (newContent) => {
  if (element.suggestions.list) {
    element.suggestions.list.innerHTML = '';

    newContent.forEach((content) => {
      appendElementContent(element.suggestions.list, content);
    });
  }

  openSuggestionsList();
  handleSuggestionsListNavigation();
};

/**
 * Open the suggestions list element
 * @returns {void}
 */
const openSuggestionsList = () => {
  if (element.suggestions.wrapper) {
    removeClassToElement(element.suggestions.wrapper, classes.hidden);
  }
};

/**
 * Close the suggestions list element
 * @returns {void}
 */
const closeSuggestionsList = () => {
  if (element.suggestions.wrapper) {
    addClassToElement(element.suggestions.wrapper, classes.hidden);
  }
  if (controller) {
    controller.abort();
  }
};

/**
 * Handle the suggestions list navigation with the keyboard
 * @returns {void}
 */
const handleSuggestionsListNavigation = () => {
  let currentIndexForSuggestionsList = -1;
  const listItems = element.suggestions.list.querySelectorAll('li');
  controller = new AbortController();
  if (listItems.length > 0) {
    selectedIndexForSuggestionsList = 0;

    document.addEventListener(
      'keydown',
      function (event) {
        const eventKey = event.key;

        if (
          eventKey !== 'ArrowDown' &&
          eventKey !== 'ArrowUp' &&
          eventKey !== 'Tab'
        )
          return;

        event.preventDefault();

        if (eventKey === 'ArrowDown') {
          if (currentIndexForSuggestionsList < listItems.length - 1) {
            currentIndexForSuggestionsList++;
          } else {
            currentIndexForSuggestionsList = 0;
          }
        } else if (eventKey === 'ArrowUp') {
          if (currentIndexForSuggestionsList > 0) {
            currentIndexForSuggestionsList--;
          } else {
            currentIndexForSuggestionsList = listItems.length - 1;
          }
        } else if (eventKey === 'Tab') {
          if (currentIndexForSuggestionsList < listItems.length - 1) {
            currentIndexForSuggestionsList++;
          } else {
            currentIndexForSuggestionsList = 0;
          }
        }

        listItems.forEach((item) =>
          removeClassToElement(item, classes.is.selected)
        );

        if (
          currentIndexForSuggestionsList >= 0 &&
          currentIndexForSuggestionsList < listItems.length
        ) {
          addClassToElement(
            listItems[currentIndexForSuggestionsList],
            classes.is.selected
          );
          selectedIndexForSuggestionsList = currentIndexForSuggestionsList;
        }
      },
      { signal: controller.signal }
    );
  }
};

/**
 * Handle the place selected event and fill the address data
 * @param {Place} place - The selected place
 * @returns {void}
 */
const onPlaceSelected = async (place) => {
  if (element.input.search) {
    await place.fetchFields({
      fields: ['formattedAddress']
    });

    const chars =
      element.input.search.value.trim().length - minCharactersForAutocomplete;

    pushOnDataLayer({
      event: 'google_autocomplete_places',
      chars: chars,
      origin: `${environment}-pick-up-stores`
    });

    element.input.search.value = place.formattedAddress;
  }

  closeSuggestionsList();
  handleAutocompleteAddress(place);
  resetSessionToken();
};

/**
 * Refresh the session token for the autocomplete request
 * @returns {void}
 */
const refreshSessionToken = () => {
  sessionAutocomplete.token = new AutocompleteSessionToken();
  sessionAutocomplete.closed = false;
  requestAutocomplete.sessionToken = sessionAutocomplete.token;
};

/**
 * Reset the session token for the autocomplete request
 * @returns {void}
 */
const resetSessionToken = () => {
  sessionAutocomplete.token = null;
  sessionAutocomplete.closed = false;
  requestAutocomplete.sessionToken = null;
};

/**
 * Get the first prediction from the autocomplete suggestions and handle it
 * @returns {void}
 */
const getSelectedOrFirstPrediction = async () => {
  const suggestions = await getSuggestions();

  if (suggestions.length > 0) {
    const selectedSuggestion = suggestions[selectedIndexForSuggestionsList];
    const place = selectedSuggestion.placePrediction;

    lastPrediction = selectedSuggestion;
    onPlaceSelected(place.toPlace());
  }
};

/**
 * Handle the autocomplete address and fill the address data
 * @param {Place} place - The selected place
 * @returns {void}
 */
const handleAutocompleteAddress = async (place) => {
  await place.fetchFields({
    fields: ['addressComponents', 'location']
  });

  const location = 'location' in place ? place.location : null;
  const latitude = location ? location.lat() : '';
  const longitude = location ? location.lng() : '';
  const addressData = {
    latitude: latitude,
    longitude: longitude,
    countryCode: '',
    postalCode: ''
  };

  if ('addressComponents' in place) {
    for (const component of place.addressComponents) {
      const componentType = component.types[0];

      switch (componentType) {
        case 'country':
          addressData.countryCode = component.shortText;
          break;

        case 'postal_code':
          addressData.postalCode = component.longText;
          break;
      }
    }
  }

  if (sessionAutocomplete.token) {
    sessionAutocomplete.closed = true;
  }

  fillAddressData(addressData);
  handleFormSubmit();
};

/**
 * Fill the address data in the form inputs
 * @param {object} addressData - The address data
 * @returns {void}
 */
const fillAddressData = (addressData) => {
  if (element.input.latitude) {
    element.input.latitude.value = addressData.latitude;
  }

  if (element.input.longitude) {
    element.input.longitude.value = addressData.longitude;
  }

  if (element.input.countryCode) {
    element.input.countryCode.value = addressData.countryCode;
  }

  if (element.input.postalCode) {
    element.input.postalCode.value = addressData.postalCode;
  }
};

/**
 * Get the form data from the form inputs
 * @returns {object} The form data
 */
const getFormData = () => {
  const inputs = element.form.querySelectorAll('input');
  const data = {};

  inputs.forEach((input) => {
    const name = input.name;
    const value = input.value;

    if (name) {
      data[name] = value;
    }
  });

  data.environment = environment;

  return data;
};

/**
 * Paint the stores list with the new content
 * @param {string} html - The new content to paint
 * @returns {void}
 */
const paintStoresList = (html) => {
  if (element.list && element.listResults) {
    removeClassToElement(element.list, classes.is.empty);
    removeClassToElement(element.list, classes.not.results);
    element.listResults.innerHTML = html;

    if (html.length === 0) {
      addClassToElement(element.list, classes.not.results);
    }
    initStoreCards();
  } else {
    console.warn('Element list not found');
  }
};

/**
 * Handle the store card click event
 * @param {HTMLElement} element - The store card element
 * @returns {void}
 */
const handleStoreCardClick = (element) => {
  toggleClassToElement(element, classes.is.open);
};

/**
 * Handle the store button click event
 * @param {Event} event - The store button click event
 * @returns {void}
 */
const handleStoreButtonClick = (event) => {
  const button = event.currentTarget;
  const storeId = button?.dataset?.storeId;
  const carrier = button?.dataset?.carrier;
  const isPickUpPoint = button?.dataset?.isPickUpPoint;
  const formData = new FormData();

  formData.append('storeId', storeId);
  formData.append('carrier', carrier);
  formData.append('isPickUpPoint', isPickUpPoint);

  if (environment === environments.pdp) {
    const productId = getCurrentProductId();

    if (storeId && productId) {
      formData.append('productId', productId);

      addClassToElement(button, classes.is.loading);

      axios
        .post(globalVariables.endpoint.setPickUpStore, formData)
        .then((response) => {
          const { data } = response;
          const { messageHtml, success } = data;

          if (success) {
            setMessageStoreSelected(messageHtml);
          }
          document.dispatchEvent(new CustomEvent(customEvent.sidebar.close));
          document.dispatchEvent(new CustomEvent(customEvent.sidebar.init));
        })
        .catch((error) => {
          console.error(error);
        })
        .then(() => {
          removeClassToElement(button, classes.is.loading);
        });
    } else {
      console.warn('store ID or product ID not found');
    }
  } else if (environment === environments.checkout) {
    if (storeId) {
      addClassToElement(button, classes.is.loading);

      axios
        .post(globalVariables.endpoint.setPickUpStore, formData)
        .then((response) => {
          const { data } = response;
          const { pointName = '', pointLocation = '' } = data;

          document.dispatchEvent(
            new CustomEvent(customEvent.updatePickUpStoreSelectedForShipping, {
              detail: {
                pointName,
                pointLocation
              }
            })
          );

          document.dispatchEvent(
            new CustomEvent(customEvent.checkout.selectStorePickupMethod)
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .then(() => {
          removeClassToElement(button, classes.is.loading);
        });
    } else {
      console.warn('store ID not found');
    }
  }
};

/**
 * Update the results message with the new content
 * @param {string} message - The new content to update
 * @returns {void}
 */
const updateResultsMessage = (message) => {
  if (element.results.wrapper) {
    removeClassToElement(element.results.wrapper, classes.hidden);
  }

  if (element.results.message) {
    element.results.message.textContent = message;
  }
};

/**
 * Listen the stores filter events
 * @returns {void}
 */
const listenStoresFilter = () => {
  if (element.filter.wrapper) {
    if (element.store.card.length > 0 && someStoreHasAvailableProduct()) {
      removeClassToElement(element.filter.wrapper, classes.hidden);
    } else {
      addClassToElement(element.filter.wrapper, classes.hidden);
    }
  }

  if (element.filter.input) {
    listenDOMEvent(element.filter.input, 'change', handleFilterChange);
  }

  if (element.filter.optical) {
    listenDOMEvent(element.filter.optical, 'change', handleFilterOpticalChange);
  }
};

/**
 * Handle the filter change event
 * @param {Event} event - The filter change event
 * @returns {void}
 */
const handleFilterChange = (event) => {
  const filter = event.currentTarget;
  const checked = filter.checked;
  const unavailableStoreCards = Array.from(element.store.card).filter(
    (card) => card?.dataset?.hasProduct === 'false'
  );

  unavailableStoreCards.forEach((card) => {
    if (checked) {
      addClassToElement(card, 'hw-show-available');
    } else {
      removeClassToElement(card, 'hw-show-available');
    }
  });
};

/**
 * Handle the filter change event
 * @param {Event} event - The filter change event
 * @returns {void}
 */
const handleFilterOpticalChange = (event) => {
  const filter = event.currentTarget;
  const checked = filter.checked;
  element.store.notOptical.forEach((card) => {
    if (checked) {
      addClassToElement(card, 'hw-show-optics');
    } else {
      removeClassToElement(card, 'hw-show-optics');
    }
  });
};

/**
 * Set the loader state
 * @param {boolean} isLoading - The loader state
 * @returns {void}
 */
const setLoader = (isLoading) => {
  if (isLoading) {
    addClassToElement(element.wrapper, classes.is.loading);
  } else {
    removeClassToElement(element.wrapper, classes.is.loading);
  }
};

/**
 * Set the message store selected with the new content
 * @param {string} html - The new content to set
 * @returns {void}
 */
const setMessageStoreSelected = (html) => {
  if (html && element.message) {
    element.message.innerHTML = html;
  }
};

/**
 * Get the current product ID from the wrapper element
 * @returns {string} The current product ID
 */
const getCurrentProductId = () => {
  if (element.wrapper) {
    return element.wrapper?.dataset?.productId;
  }

  return '';
};

/**
 * Listen the button geolocation event
 * @returns {void}
 */
const listenButtonGeolocation = () => {
  if (element.button.geolocation && navigator.geolocation) {
    removeClassToElement(element.button.geolocation, classes.hidden);
    listenDOMEvent(
      element.button.geolocation,
      'click',
      handleButtonGeolocation
    );
  }
};

/**
 * Handle the button geolocation event
 * @returns {void}
 */
const handleButtonGeolocation = () => {
  setLoader(true);

  navigator.geolocation.getCurrentPosition(
    (position) => {
      const pos = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      const addressData = {
        latitude: pos.lat,
        longitude: pos.lng,
        countryCode: '',
        postalCode: ''
      };

      fillAddressData(addressData);
      handleFormSubmit();
    },
    (error) => {
      setLoader(false);
      console.error('ERROR(' + error.code + '): ' + error.message);
    }
  );
};

/**
 * Launch the form submit for the current store selected
 * @returns {void}
 */
const launchForCurrentStoreSelected = () => {
  const initialLatitude = element.input.latitude?.value;
  const initialLongitude = element.input.longitude?.value;

  if (initialLatitude && initialLongitude) {
    handleFormSubmit();
  }
};

/**
 * Check if some store has an available product
 * @returns {boolean} The result of the check
 */
const someStoreHasAvailableProduct = () => {
  return Array.from(element.store.card).some(
    (card) => card?.dataset?.hasProduct === 'true'
  );
};

const forceCloseSessionWithLastPrediction = async () => {
  if (
    sessionAutocomplete.token &&
    !sessionAutocomplete.closed &&
    lastPrediction
  ) {
    const place = lastPrediction.placePrediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });
    sessionAutocomplete.closed = true;
  }
};

/**
 * =================
 * ONLY FOR CHECKOUT
 * =================
 */

export { initPickUpStoresLocator };
